<template>
  <div
    class="audio-pub"
    :class="{ 'is-selected': isSelected, 'has-bg': infos.img }"
    :style="bgStyle"
  >
    <div class="bg-overlay" @click="onBodyTap" />

    <!-- Flash icon shown briefly on tap (like TikTok) -->
    <transition name="flash">
      <div v-if="showFlash" class="play-flash">
        <svg v-if="isPlaying" viewBox="0 0 24 24" fill="white" width="72" height="72"><path d="M8 5v14l11-7z"/></svg>
        <svg v-else           viewBox="0 0 24 24" fill="white" width="72" height="72"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
      </div>
    </transition>

    <!-- ── Right action bar ── -->
    <div class="action-bar" @click.stop>
      <button class="action-btn avatar-btn" @click="emit('addToPlaylist', props.infos.userId)">
        <div class="avatar-ring">
          <img src="./img/playlist.svg" class="avatar-img" />
        </div>
        <span class="follow-dot">+</span>
      </button>

      <button class="action-btn" @click="emit('comment', props.infos.userId)">
        <svg viewBox="0 0 24 24" class="action-icon" fill="currentColor">
          <path d="M21 6.5C21 5.12 19.88 4 18.5 4h-13C4.12 4 3 5.12 3 6.5v8C3 15.88 4.12 17 5.5 17H7l3 4 3-4h5.5c1.38 0 2.5-1.12 2.5-2.5v-8z"/>
        </svg>
        <span class="action-count">{{ formatCount(localStats.comments) }}</span>
      </button>

      <button class="action-btn" :class="{ liked: isLiked }" @click="emit('like')">
        <svg viewBox="0 0 24 24" class="action-icon" fill="currentColor">
          <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
        </svg>
        <span class="action-count">{{ formatCount(props.infos.likes.length) }}</span>
      </button>

      <button class="action-btn" @click="emit('share',props.infos.userId)">
        <svg viewBox="0 0 24 24" class="action-icon" fill="currentColor">
          <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/>
        </svg>
        <span class="action-count">{{ formatCount(localStats.shares) }}</span>
      </button>

      <div class="disc-btn" :class="{ spinning: isPlaying }" @click="emit('publisher-click', props.infos.userId)">
        <img v-if="userInfos.profilImgMini1" :src="store.state.baseUrl+'images/'+ userInfos.profilImgMini1" class="disc-img" alt="" />
        <div v-else class="disc-placeholder">
          <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/></svg>
        </div>
      </div>
    </div>

    <!-- ── Bottom info — stops tap propagation so it doesn't toggle play ── -->
    <div class="bottom-info" @click="onBodyTap">
      <button class="publisher-name" @click.stop @click="emit('publisher-click', props.infos.userId)">
        @{{ userInfos?.pseudo }}
        <span class="pub-badge">{{ infos.type === 'private' ? '🔒' : '🌐' }}</span>
      </button>
      <p v-if="infos.text" class="pub-text">{{ infos.text }}</p>
      <div v-if="infos.tags?.length" class="tags">
        <span v-for="tag in infos.tags" :key="tag" class="tag">#{{ tag }}</span>
      </div>
      <div class="meta-row">
        <span>👁 {{ formatCount(props.infos.views.length) }} </span>
        <span>{{ timeAgo }}</span>
      </div>
    </div>

    <!-- ── Viz zone: click here to toggle controls overlay ── -->
    <div
      class="viz-container"
      :class="{ 'viz-visible': isActive }"
      ref="vizContainerRef"
      @click.stop="handleVizClick"
    >
      <!-- replace the <canvas> with this, in both files' viz container -->
      <div class="idle-bars" v-show="isPlaying">
        <span></span><span></span><span></span><span></span>
      </div>
    </div>

    <!-- ── Controls overlay — click.stop so taps here don't toggle play ── -->
    <transition name="fade">
      <div class="controls-overlay" @click.stop>
        <div v-show="showControls" class="seek-btns">
          <button class="seek-btn" @click.stop="seekBackward">
            <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
              <path d="M11.99 5V1l-5 5 5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6h-2c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
            </svg>
            <span class="seek-label">10</span>
          </button>
          <button class="seek-btn" @click.stop="seekForward">
            <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
              <path d="M12.01 5V1l5 5-5 5V7c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z"/>
            </svg>
            <span class="seek-label">10</span>
          </button>
        </div>
        <div
          class="progress-wrap"
          ref="progressRef"
          @mousedown="onProgressMousedown"
          @touchstart.prevent="onProgressTouchstart"
        >
          <div class="progress-bg">
            <div class="progress-fill"  ref="progressFillRef" />
            <div v-show="showControls" class="progress-thumb" ref="progressThumbRef" />
          </div>
          <div v-show="showControls" class="time-row">
            <span>{{ formatTime(currentTime) + ' / ' + formatTime(infos.duration || 0)}}</span>
          </div>
        </div>

        <div v-show="showControls" class="ctrl-row">
          <button class="ctrl-btn speed-btn" @click="changeSpeed(-0.1)">
            <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M20 12l-1.41-1.41L13 16.17V4h-2v12.17l-5.58-5.59L4 12l8 8 8-8z"/></svg>
          </button>
          <div class="speed-display">{{ currentSpeed.toFixed(1) }}×</div>
          <button class="ctrl-btn speed-btn" @click="changeSpeed(0.1)">
            <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M4 12l1.41 1.41L11 7.83V20h2V7.83l5.58 5.59L20 12l-8-8-8 8z"/></svg>
          </button>
          <div style="flex:1" />
 <!--          <button class="ctrl-btn play-btn" @click="togglePlay">
            <svg v-if="!isPlaying" viewBox="0 0 24 24" fill="currentColor" width="22" height="22"><path d="M8 5v14l11-7z"/></svg>
            <svg v-else viewBox="0 0 24 24" fill="currentColor" width="22" height="22"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
          </button>
          <button class="ctrl-btn" @click="toggleMute">
            <svg v-if="!isMuted" viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>
            <svg v-else viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>
          </button> -->
        </div>
      </div>
    </transition>

    <audio
      ref="htmlAudioRef"
      crossorigin="anonymous"
      @timeupdate="onTimeUpdate"
      @ended="onEnded"
      preload="none"
    />
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { ref, computed, watch, onMounted, onUnmounted, nextTick, markRaw } from 'vue'
import { isNativeAudio, NativeAudioElement } from '@/plugin/nativeAudio'
import { useStore } from 'vuex'
import { setupMediaControls, updatePlaybackState, destroyMediaControls } from '@/plugin/mediaControls'

const store = useStore()

const props = defineProps({
  infos: {
    type: Object,
    default: () => ({
      userId: 'unknown', text: '', img: null, imgMini: null, audio: null,
      duration: 0, curveType: 0, curveColor: 0, type: 'public',
      stats: { shares: 0, views: 0, likes: 0, comments: 0 },
      tags: [], createdAt: null, deletedAt: null,
    }),
  },
  active: {
    type: Boolean,
    default: false
  },
  index: {
    type: Number,
    default: 0
  }
})



const emit      = defineEmits(['publisher-click', 'comment', 'like', 'share','addToPlaylist','played','ended'])
const infos     = computed(() => props.infos)
const userInfos = computed(() => store.state.users[props.infos.userId] || {})

// ─── Refs ─────────────────────────────────────────────────────────────────────
const vizContainerRef  = ref(null)
const progressRef      = ref(null)
const progressFillRef  = ref(null)
const progressThumbRef = ref(null)
const isPlaying        = ref(false)
const isSelected       = ref(false)
const isVisible        = ref(false)
const showControls     = ref(false)
const showFlash        = ref(false)
const isLiked          = ref(false)
const isShared         = ref(false)
const currentTime      = ref(0)
const currentSpeed     = ref(1.0)
const localStats       = ref({ ...props.infos.stats })

const isActive = computed(() => isVisible.value && (isPlaying.value || isSelected.value))

let inactivityTimer     = null
let flashTimer          = null
let isDragging          = false
let intersectionObs     = null
let lastTimeUpdate      = 0
let lastPositionUpdate  = 0   // ← separate tracker for media controls sync

const htmlAudioRef = ref(null)

const nativeEl = isNativeAudio
  ? markRaw(new NativeAudioElement(
      props.infos.audio,
      () => ({
        title:   props.infos.text || props.infos.title || 'Publication',
        artist:  userInfos.value?.pseudo || props.infos.author || 'Wedio',
        artwork: props.infos.imgMini ? store.state.baseUrl + 'images/' + props.infos.imgMini : (props.infos.img ? store.state.baseUrl + 'images/' + props.infos.img : ''),
      }),
      'publications'
    ))
  : null
if (nativeEl) {
  nativeEl.addEventListener('timeupdate', onTimeUpdate)
  nativeEl.addEventListener('ended', onEnded)
}
// every existing `audioRef.value` now resolves to the native shim on Android
const audioRef = computed(() => nativeEl || htmlAudioRef.value)

// ── Media controls (web + Android bridge) ─────────────────────────────────────
function syncPlaybackState() {
  const el  = audioRef.value
  const dur = el?.duration || props.infos.duration || 0
  const pos = el?.currentTime || currentTime.value || 0
  updatePlaybackState({
    isPlaying: isPlaying.value,
    position:  pos,
    duration:  dur,
    speed:     currentSpeed.value || 1,
  })
}

function initMediaControls() {
  setupMediaControls({
    title:  props.infos.text || 'Publication',
    artist: userInfos.value?.pseudo || '',
    cover:  props.infos.imgMini
              ? store.state.baseUrl + 'images/' + props.infos.imgMini
              : '',
    handlers: {
      onPlay:  () => { if (!isPlaying.value) togglePlay() },
      onPause: () => { if (isPlaying.value)  togglePlay() },
      onStop:  () => stopPlay(),
      onNext:  () => window.dispatchEvent(new CustomEvent('media:next')),
      onPrev:  () => window.dispatchEvent(new CustomEvent('media:prev')),
      onTrackChanged: (audioId) => window.dispatchEvent(new CustomEvent('media:track', { detail: audioId })),
      onSeek:  (t) => {
        const el = audioRef.value
        if (!el || !isFinite(t)) return
        const dur     = el.duration || props.infos.duration || 0
        const clamped = Math.min(Math.max(t, 0), dur)
        el.currentTime    = clamped
        currentTime.value = clamped
        updateProgressDOM(calcPct())
        syncPlaybackState()
        startHideTimer()
      },
      onForward: () => { seekForward() },
      onRewind:  () => { seekBackward() },
    }
  })
  // Delay first sync to let el.duration settle
  setTimeout(() => syncPlaybackState(), 300)
}

// ─────────────────────────────────────────────────────────────────────────────

function setIsLiked() {
  const likes = props.infos.likes
  const like  = likes.find(lk => lk.userId === store.state.user.uid)
  isLiked.value = like ? true : false
}
setIsLiked()

// ─── Computed ─────────────────────────────────────────────────────────────────
const bgStyle = computed(() => {
  if (!props.infos.img) return { backgroundImage: `url(${require('./img/publication.svg')})` }
  return { backgroundImage: `url(${store.state.baseUrl}images/${props.infos.img})` }
})

const timeAgo = computed(() => {
  if (!props.infos.createdAt) return ''
  const s = (Date.now() - new Date(props.infos.createdAt)) / 1000 | 0
  if (s < 60)    return `${s}s`
  if (s < 3600)  return `${s / 60 | 0}m`
  if (s < 86400) return `${s / 3600 | 0}h`
  return `${s / 86400 | 0}d`
})

// ─── Direct DOM progress ──────────────────────────────────────────────────────
function updateProgressDOM(pct) {
  if (progressFillRef.value)  progressFillRef.value.style.width = pct + '%'
  if (progressThumbRef.value) progressThumbRef.value.style.left = pct + '%'
}
function calcPct() {
  const dur = audioRef.value?.duration || props.infos.duration || 1
  return Math.min(100, (currentTime.value / dur) * 100)
}

// ─── One audio at a time ──────────────────────────────────────────────────────
const myId = props.infos.audio || Math.random().toString(36).slice(2)

watch(() => store.state.playingAudioId, (id) => {
  if (id !== myId && isPlaying.value) {
    audioRef.value?.pause()
    isPlaying.value = false
  }
})

// ─── Controls visibility ──────────────────────────────────────────────────────
function startHideTimer() {
  clearTimeout(inactivityTimer)
  inactivityTimer = setTimeout(() => { showControls.value = false }, 3500)
}

function handleVizClick() {
  isSelected.value   = true
  showControls.value = true
  startHideTimer()
}

// ─── Body tap = play / pause ──────────────────────────────────────────────────
function onBodyTap() {
  togglePlay()
  showFlash.value = true
  clearTimeout(flashTimer)
  flashTimer = setTimeout(() => { showFlash.value = false }, 600)
}

// ─── Playback ─────────────────────────────────────────────────────────────────
async function togglePlay() {
  const el = audioRef.value
  if (!el || !props.infos.audio) return

  if (!el.src || !el.src.includes(props.infos.audio)) {
    el.src = store.state.baseUrl + 'audios/' + props.infos.audio
  }

  if (isPlaying.value) {
    el.pause()
    isPlaying.value = false
    store.commit('setCurrentPlayer', null)
    store.commit('setPlayingAudioId', null)
    store.commit('player/setIsPlaying', false)
    syncPlaybackState()
  } else {
    store.commit('setPlayingAudioId', myId)
    try {
      await el.play()
      isPlaying.value = true
      initMediaControls()
      store.commit('setCurrentPlayer', 'publication')
      emit('played')
      store.commit('player/setIsPlaying', true)
      store.commit('player/setDuration', el.duration || props.infos.duration || 0)
      store.commit('player/setMeta', {
        userName:   userInfos.value?.pseudo || '',
        userAvatar: userInfos.value?.profilImgMini1 || '',
        text:       props.infos.text || '',
        img:        props.infos.img  || '',
        imgMini:    props.infos.imgMini || '',
      })
      showControls.value = true
      startHideTimer()
    } catch (err) {
      console.warn('Playback failed:', err)
      store.commit('setPlayingAudioId', null)
      store.commit('player/setIsPlaying', false)
    }
  }
}

function changeSpeed(delta) {
  let s = currentSpeed.value + delta
  if (s < 0.5) s = 0.5
  if (s > 2.0) s = 2.0
  currentSpeed.value = Math.round(s * 10) / 10
  if (audioRef.value) audioRef.value.playbackRate = currentSpeed.value
  startHideTimer()
}

// ─── Progress bar ─────────────────────────────────────────────────────────────
function getRatio(clientX) {
  const rect = progressRef.value.getBoundingClientRect()
  let r = (clientX - rect.left) / rect.width
  if (r < 0) r = 0; if (r > 1) r = 1
  return r
}
function applySeek(ratio) {
  const el  = audioRef.value
  const dur = el?.duration || props.infos.duration || 0
  if (!dur) return
  currentTime.value = ratio * dur
  if (el) el.currentTime = ratio * dur
  updateProgressDOM(ratio * 100)
  syncPlaybackState()
}
function onProgressMousedown(e)  { isDragging = true;  applySeek(getRatio(e.clientX)); startHideTimer() }
function onProgressMousemove(e)  { if (isDragging) applySeek(getRatio(e.clientX)) }
function onProgressMouseup(e)    { if (!isDragging) return; isDragging = false; applySeek(getRatio(e.clientX)) }
function onProgressTouchstart(e) { isDragging = true;  applySeek(getRatio(e.touches[0].clientX)); startHideTimer() }
function onProgressTouchmove(e)  { if (!isDragging) return; e.preventDefault(); applySeek(getRatio(e.touches[0].clientX)) }
function onProgressTouchend(e)   { if (!isDragging) return; isDragging = false; applySeek(getRatio(e.changedTouches[0].clientX)) }

function onTimeUpdate() {
  if (isDragging) return
  const now = Date.now()
  if (now - lastTimeUpdate < 80) return
  lastTimeUpdate    = now
  currentTime.value = audioRef.value?.currentTime || 0
  updateProgressDOM(calcPct())
  store.commit('player/setCurrentTime', currentTime.value)
  const dur = audioRef.value?.duration
  if (dur && !isNaN(dur) && dur !== store.state.player.duration) {
    store.commit('player/setDuration', dur)
  }
  // Sync OS player every 2 seconds using separate tracker
  if (now - lastPositionUpdate > 2000) {
    lastPositionUpdate = now
    syncPlaybackState()
  }
}

function onEnded() {
  isPlaying.value   = false
  currentTime.value = 0
  updateProgressDOM(0)
  store.commit('setPlayingAudioId', null)
  showControls.value = false
  store.commit('player/setIsPlaying', false)
  store.commit('setCurrentPlayer', null)
  destroyMediaControls()
  emit('ended')
}

// ── Public API for Publications.vue ──────────────────────────────────────────
async function startPlay(ghostEl = null) {
  const el = audioRef.value
  if (!el || !props.infos.audio || isPlaying.value) return

  // Reuse ghost if valid and already buffered
  if (
    ghostEl &&
    ghostEl.src &&
    ghostEl.src.includes(props.infos.audio) &&
    ghostEl.crossOrigin === 'anonymous'
  ) {
    if (!el.src || !el.src.includes(props.infos.audio)) {
      el.preload = 'auto'
      el.src     = ghostEl.src   // same URL, browser cache already warm
    }
    ghostEl.src = ''
  } else {
    if (!el.src || !el.src.includes(props.infos.audio)) {
      el.src = store.state.baseUrl + 'audios/' + props.infos.audio
    }
  }

  store.commit('setPlayingAudioId', myId)
  try {
    await el.play()
    isPlaying.value = true
    initMediaControls()
    store.commit('setCurrentPlayer', 'publication')
    emit('played')
    store.commit('player/setIsPlaying', true)
    store.commit('player/setDuration', el.duration || props.infos.duration || 0)
    store.commit('player/setMeta', {
      userName:   userInfos.value?.pseudo || '',
      userAvatar: userInfos.value?.profilImgMini1 || '',
      text:       props.infos.text || '',
      img:        props.infos.img  || '',
      imgMini:    props.infos.imgMini || '',
    })
  } catch (err) {
    console.warn('Auto-play failed:', err)
    store.commit('setPlayingAudioId', null)
    store.commit('player/setIsPlaying', false)
  }
}

function stopPlay() {
  const el = audioRef.value
  if (!el || !isPlaying.value) return
  el.pause()
  isPlaying.value    = false
  showControls.value = false
  destroyMediaControls()
  store.commit('setPlayingAudioId', null)
  store.commit('player/setIsPlaying', false)
  store.commit('setCurrentPlayer', null)
}

// ─── Misc ─────────────────────────────────────────────────────────────────────
function formatCount(n = 0) {
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M'
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K'
  return String(n)
}
function formatTime(s = 0) {
  const m = s / 60 | 0, sec = s % 60 | 0
  return `${m}:${sec < 10 ? '0' + sec : sec}`
}

function seekBackward() {
  const el  = audioRef.value
  const dur = el?.duration || props.infos.duration || 0
  if (!dur) return
  const t = Math.max(0, (el?.currentTime || currentTime.value) - 10)
  if (el) el.currentTime = t
  currentTime.value = t
  updateProgressDOM(calcPct())
  syncPlaybackState()
  startHideTimer()
}

function seekForward() {
  const el  = audioRef.value
  const dur = el?.duration || props.infos.duration || 0
  if (!dur) return
  const t = Math.min(dur, (el?.currentTime || currentTime.value) + 10)
  if (el) el.currentTime = t
  currentTime.value = t
  updateProgressDOM(calcPct())
  syncPlaybackState()
  startHideTimer()
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────
onMounted(async () => {
  await nextTick()

  intersectionObs = new IntersectionObserver((entries) => {
    const e = entries[0]
    isVisible.value = e.isIntersecting
    if (!e.isIntersecting && !isPlaying.value) {
      isSelected.value   = false
      showControls.value = false
    }
  }, { threshold: 0.1 })
  if (vizContainerRef.value) intersectionObs.observe(vizContainerRef.value)

  window.addEventListener('mouseup',   onProgressMouseup)
  window.addEventListener('mousemove', onProgressMousemove)
  window.addEventListener('touchend',  onProgressTouchend)
  window.addEventListener('touchmove', onProgressTouchmove, { passive: false })
})

onUnmounted(() => {
  clearTimeout(inactivityTimer)
  clearTimeout(flashTimer)
  intersectionObs?.disconnect()
  window.removeEventListener('mouseup',   onProgressMouseup)
  window.removeEventListener('mousemove', onProgressMousemove)
  window.removeEventListener('touchend',  onProgressTouchend)
  window.removeEventListener('touchmove', onProgressTouchmove)
  destroyMediaControls()
  //-- native ----
  nativeEl?.dispose()
})

watch(() => props.infos.stats, v => { localStats.value = { ...v } }, { deep: true })

watch(() => store.state.currentPlayer, (val) => {
  if (val !== 'publication' && isPlaying.value) {
    audioRef.value?.pause()
    isPlaying.value    = false
    showControls.value = false
    syncPlaybackState()
  }
})

defineExpose({
  isLiked,
  isShared,
  startPlay,
  stopPlay,
  togglePlay,
  index: computed(() => props.index)
})
</script>

<style scoped>
.audio-pub {
  position: relative; width: 100%; max-width: 460px;
  height: 100%;
  border-radius: 0; overflow: hidden; background: #0d1130;
  display: flex; flex-direction: column; justify-content: flex-end;
  font-family: 'Outfit', sans-serif;
  user-select: none;
  padding-bottom: 111px;
}
.audio-pub.has-bg { background-size: cover; background-position: center; }

.bg-overlay {
  position: absolute; inset: 0; z-index: 1;
  bottom: 0px;
  height: 100%;width: 100%;
  background: linear-gradient(to top, rgba(10,14,38,0.97) 0%, rgba(10,14,38,0.55) 45%, rgba(10,14,38,0.10) 100%);
}

/* ── Flash icon on tap ── */
.play-flash {
  position: absolute; z-index: 20;
  width: 100%; height: 100%;
  bottom:0px;
  display: flex; align-items: center; justify-content: center;
  pointer-events: none;
}
.play-flash svg {
  opacity: 0.85;
}
.flash-enter-active  { transition: opacity 0.05s, transform 0.05s; }
.flash-leave-active  { transition: opacity 0.5s ease-out, transform 0.5s ease-out; }
.flash-enter-from    { opacity: 0; transform: scale(0.6); }
.flash-leave-to      { opacity: 0; transform: scale(1.4); }

/* ── Action bar — above tap zone ── */
.action-bar {
  position: absolute; right: 12px; bottom: 0px; z-index: 10;
  margin-bottom:130px ;
  display: flex; flex-direction: column; align-items: center;
  flex-wrap: nowrap;
}
.action-bar > * + * { margin-top: 22px; }
.action-btn {
  display: flex; flex-direction: column; align-items: center; gap: 4px;
  background: none; border: none; color: rgba(255,255,255,0.9);
  cursor: pointer; padding: 0; transition: transform 0.15s;
}
.action-btn:active             { transform: scale(0.88); }
.action-btn.liked .action-icon { color: #ff4d6d; filter: drop-shadow(0 0 6px #ff4d6d); }
.action-icon  { width: 32px; height: 32px; }
.action-count { font-size: 14px; font-weight: 600; letter-spacing: 0.3px; }

.avatar-btn  { position: relative; }
.avatar-ring {
  width: 54px; height: 54px; border-radius: 50%;
  border: 2px solid #fff; overflow: hidden; background: #1a2150;
  display: flex; align-items: center; justify-content: center;
}
.avatar-img         { width: 100%; height: 100%; object-fit: cover; }
.avatar-placeholder { font-size: 20px; font-weight: 700; color: #fff; }
.follow-dot {
  position: absolute; bottom: -4px; left: 50%; transform: translateX(-50%);
  width: 24px; height: 24px; border-radius: 50%; background: #ff4d6d;
  color: #fff; font-size: 18px; line-height: 24px; text-align: center;
  font-weight: 700; border: 2px solid #0a0e26;
  display: flex;justify-content: center;align-items: center;
}
.disc-btn {
  width: 50px; height: 50px; border-radius: 50%;
  border: 3px solid rgba(255,255,255,0.3); overflow: hidden; background: #1a2150;
  display: flex; align-items: center; justify-content: center; color: rgba(255,255,255,0.7);
  cursor: pointer;
}
.disc-img         { width: 100%; height: 100%; object-fit: cover; }
.disc-placeholder { display: flex; align-items: center; justify-content: center; }
.disc-placeholder svg { width: 22px; height: 22px; }
.spinning         { animation: spin 3s linear infinite; }
@keyframes spin   { to { transform: rotate(360deg); } }

/* ── Bottom info ── */
.bottom-info {
  position: relative; z-index: 5;
  padding: 0 16px 10px; max-width: calc(100% - 78px);
}
.publisher-name {
  background: none; border: none; cursor: pointer; color: #fff;
  font-family: 'Outfit', sans-serif; font-size: 17px; font-weight: 700;
  padding: 0; margin: 0 0 5px; display: flex; align-items: center;
}
.publisher-name:hover { opacity: 0.8; }
.pub-badge { font-size: 15px; margin-left: 6px; }
.pub-text  {
  color: rgba(255,255,255,0.82); font-size: 15px; line-height: 1.45; margin: 0 0 5px;
  display: -webkit-box; -webkit-box-orient: vertical; overflow: hidden;
}
.tags { display: flex; flex-wrap: wrap; margin-bottom: 5px; }
.tags > * + * { margin-left: 5px; }

.tag  { font-size: 14px; color: #8ecdf7; font-weight: 600; }
.meta-row {
  display: flex; font-size: 13px; color: rgba(255,255,255,0.45);
  font-family: 'Space Mono', monospace;
}
.meta-row > * + * { margin-left: 14px; }


/* ── Viz ── */
.viz-container {
  position: relative; z-index: 6; width: 100%; height: 76px;
  opacity: 0; transition: opacity 0.4s ease; will-change: opacity;
  cursor: pointer;   /* hint that this zone is clickable */
}
.viz-container.viz-visible { opacity: 1; }
.viz-canvas { width: 100%; height: 100%; display: block; }

/* ── Controls overlay ── */
.controls-overlay {
  position: relative; z-index: 7; 
  background: linear-gradient(to top, rgba(8,12,35,0.40), transparent);
}
.progress-wrap        { padding: 0 0 4px; cursor: grab; user-select: none;  }
.progress-wrap:active { cursor: grabbing; }
.progress-bg {
  position: relative; height: 5px; background: rgba(255,255,255,0.12);
  border-radius: 99px; overflow: visible;
}
.progress-fill  {
  height: 100%; background: linear-gradient(90deg, #00e5ff, #a29bfe);
  border-radius: 99px; width: 0%; will-change: width;
}
.progress-thumb {
  position: absolute; top: 50%; transform: translate(-50%, -50%);
  width: 14px; height: 14px; border-radius: 50%; background: #fff;
  box-shadow: 0 0 8px rgba(0,229,255,0.6); pointer-events: none;
  left: 0%; will-change: left;
}
.time-row {
  display: flex; justify-content: space-between;
  font-family: 'Space Mono', monospace;
  font-size: 12px; color: rgba(255,255,255,0.4); margin-top: 5px;
  padding-left:6px ; padding-right:6px ;
}
.ctrl-row { display: flex; align-items: center; margin-top: 6px;margin-bottom: 6px; padding-left:6px ; padding:6px ;}
.ctrl-row > * + * { margin-left: 12px; }
.ctrl-btn {
  background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px; color: rgba(255,255,255,0.85); padding: 9px 12px; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
}
.ctrl-btn:hover  { background: rgba(255,255,255,0.14); }
.ctrl-btn:active { transform: scale(0.92); }
.ctrl-btn svg { width: 18px; height: 18px; }
.speed-btn { padding: 9px 11px; }
.speed-display {
  font-family: 'Space Mono', monospace; font-size: 15px; font-weight: 700;
  color: #00e5ff; min-width: 40px; text-align: center; letter-spacing: -0.5px;
}
/* .play-btn {
  background: linear-gradient(135deg, #00e5ff22, #a29bfe22);
  border-color: rgba(0,229,255,0.35); padding: 8px 12px; color: #fff;
}
.play-btn:hover { background: linear-gradient(135deg, #00e5ff44, #a29bfe44); } */

.seek-btns {
  z-index: 9;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  padding:10px;
  padding-top: 6px;
  background-color: transparent;
}
.seek-btns > * + * { margin-left: 48px; }

/* show when controls are visible */
.controls-overlay .seek-btns,
.ap-controls-visible .seek-btns {
  pointer-events: auto;
  opacity: 1;
}

.seek-btn {
  position: relative;
  background: rgba(0, 0, 0, 0.35);
  border: 1.5px solid rgba(255,255,255,0.2);
  border-radius: 50%;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(255,255,255,0.9);
}
.seek-btn:active { transform: scale(0.9); }
.seek-btn svg { width: 32px; height: 32px; }

.seek-label {
  position: absolute;
  bottom: 6px;
  font-size: 12px;
  font-weight: 700;
  color: rgba(255,255,255,0.8);
  pointer-events: none;
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.25s ease}
.fade-enter-from { opacity: 0;  }
.fade-leave-to   { opacity: 0;  }

.idle-bars {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: 100%;
  justify-content: center;
}
.idle-bars span {
  width: 4px;
  border-radius: 2px;
  background: linear-gradient(180deg, #00e5ff, #a29bfe);
  animation: idle-bar-bounce 1.1s ease-in-out infinite;
}
.idle-bars span:nth-child(1) { animation-delay: 0s;    }
.idle-bars span:nth-child(2) { animation-delay: 0.15s; }
.idle-bars span:nth-child(3) { animation-delay: 0.3s;  }
.idle-bars span:nth-child(4) { animation-delay: 0.45s; }

@keyframes idle-bar-bounce {
  0%, 100% { height: 20%; }
  50%      { height: 90%; }
}

.idle-bars span { height: 100%; transform: scaleY(0.2); transform-origin: bottom; }
@keyframes idle-bar-bounce {
  0%, 100% { transform: scaleY(0.2); }
  50%      { transform: scaleY(0.9); }
}
/* ════════════════════════════════════════════════════
   Small-screen override — restore original proportions
   ════════════════════════════════════════════════════ */
@media (max-width: 340px) {
  .audio-pub { padding-bottom: 110px; }

  .action-bar > * + * { margin-top: 18px; }
  .action-icon  { width: 28px; height: 28px; }
  .action-count { font-size: 12px; }

  .avatar-ring { width: 46px; height: 46px; }
  .avatar-placeholder { font-size: 18px; }
  .follow-dot {
    width: 20px; height: 20px; font-size: 16px; line-height: 20px;
  }

  .disc-btn { width: 42px; height: 42px; }
  .disc-placeholder svg { width: 18px; height: 18px; }

  .bottom-info { max-width: calc(100% - 70px); }

  .publisher-name { font-size: 15px; }
  .pub-badge { font-size: 13px; }
  .pub-text  { font-size: 13px; }
  .tag  { font-size: 12px; }
  .meta-row { font-size: 11px; }
  .meta-row > * + * { margin-left: 12px; }

  .viz-container { height: 68px; }
  .progress-wrap { padding: 0 0 2px;}
  .progress-bg { height: 4px; }
  .progress-thumb { width: 12px; height: 12px; }
  .time-row { font-size: 10px; }

  .ctrl-row { padding: 6px; }
  .ctrl-row > * + * { margin-left: 10px; }
  .ctrl-btn { padding: 7px 10px; }
  .ctrl-btn svg { width: 16px; height: 16px; }
  .speed-btn { padding: 7px 9px; }
  .speed-display { font-size: 13px; min-width: 36px; }

  .seek-btns > * + * { margin-left: 40px; }
  .seek-btn { width: 56px; height: 56px; }
  .seek-btn svg { width: 28px; height: 28px; }
  .seek-label { font-size: 10px; }
}
</style>